import { useEffect, useMemo, useState } from "react";
import {
  Autocomplete,
  Box,
  Button,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  TextField,
} from "@mui/material";
import { toast } from "react-toastify";
import {
  createSpecialityProcedure,
  getSpecialityProcedureCategories,
  getSpecialityProcedureOptionSuggestions,
  updateSpecialityProcedure,
} from "api/specialityProcedure";
import {
  MAX_PROCEDURE_OPTIONS,
  applyLinkedTreatmentChange,
  applyProcedureOptionChange,
  buildProcedurePayload,
  categoryOptionsOf,
  linkedTreatmentLabel,
  linkedTreatmentOptionsOf,
  procedureFormOf,
  procedureOptionSuggestions,
  pruneLinkedTreatments,
  validateProcedureForm,
} from "./specialityProcedureForm";

const sameId = (option, value) => String(option?._id) === String(value?._id);

const sameLinkedTreatment = (option, value) => {
  if (typeof option === "string" || typeof value === "string") return false;
  if (option?._id && value?._id) return String(option._id) === String(value._id);
  return lowerName(option?.name) === lowerName(value?.name) && !!lowerName(option?.name);
};
const lowerName = (value) => String(value ?? "").trim().toLowerCase();

const SpecialityProcedureModal = ({ open, onClose, data }) => {
  const procedureId = data?._id;
  const isUpdate = Boolean(procedureId);

  const [categories, setCategories] = useState([]);
  const [loadingCategories, setLoadingCategories] = useState(false);
  const [categoryId, setCategoryId] = useState("");
  const [linkedTreatments, setLinkedTreatments] = useState([]);
  const [procedureOptions, setProcedureOptions] = useState([]);
  const [optionNames, setOptionNames] = useState([]);
  const [linkedError, setLinkedError] = useState("");
  const [optionError, setOptionError] = useState("");
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (!open) return;
    const initial = procedureFormOf(data);
    setCategoryId(initial.categoryId);
    setLinkedTreatments(initial.linkedTreatments);
    setProcedureOptions(initial.procedureOptions);
    setLinkedError("");
    setOptionError("");
  }, [open, data]);

  useEffect(() => {
    if (!open) return;
    getSpecialityProcedureOptionSuggestions()
      .then((res) => {
        const list = res?.data?.data;
        setOptionNames(Array.isArray(list) ? list : []);
      })
      .catch(() => setOptionNames([]));
  }, [open]);

  useEffect(() => {
    if (!open) return;
    setLoadingCategories(true);
    getSpecialityProcedureCategories()
      .then((res) => {
        const list = res?.data?.data;
        setCategories(Array.isArray(list) ? list : []);
      })
      .catch((err) =>
        toast.error(err?.response?.data?.message || "Could not load treatment categories")
      )
      .finally(() => setLoadingCategories(false));
  }, [open]);

  const categoryOptions = useMemo(
    () => categoryOptionsOf(categories, data?.category),
    [categories, data]
  );

  const category = useMemo(
    () => categoryOptions.find((option) => String(option._id) === String(categoryId)) || null,
    [categoryOptions, categoryId]
  );

  const linkedOptions = useMemo(
    () => linkedTreatmentOptionsOf(category, linkedTreatments),
    [category, linkedTreatments]
  );

  const optionChoices = useMemo(
    () => procedureOptionSuggestions(optionNames, procedureOptions),
    [optionNames, procedureOptions]
  );

  const validationError = useMemo(
    () =>
      validateProcedureForm({
        category: categoryId,
        linkedTreatments,
        procedureOptions,
      }),
    [categoryId, linkedTreatments, procedureOptions]
  );

  const handleCategoryChange = (next) => {
    setCategoryId(next?._id || "");
    setLinkedTreatments((current) => pruneLinkedTreatments(current, next));
  };

  const handleSubmit = async () => {
    if (validationError) return;

    setSaving(true);
    const payload = buildProcedurePayload({
      category: categoryId,
      linkedTreatments,
      procedureOptions,
      original: data,
    });

    try {
      if (isUpdate) {
        await updateSpecialityProcedure(procedureId, payload);
        toast.success("Procedure updated successfully");
      } else {
        await createSpecialityProcedure(payload);
        toast.success("Procedure created successfully");
      }
      onClose("save");
    } catch (err) {
      toast.error(
        err?.response?.data?.message ||
          `Failed to ${isUpdate ? "update" : "create"} procedure`
      );
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog open={open} onClose={() => !saving && onClose("close")} fullWidth maxWidth="md">
      <DialogTitle sx={{ fontWeight: 600 }}>
        {isUpdate ? "Edit Procedure" : "Add Procedure"}
      </DialogTitle>

      <DialogContent dividers>
        <Box sx={{ display: "flex", flexDirection: "column", gap: 2.5, pt: 1 }}>
          <Autocomplete
            size="small"
            options={categoryOptions}
            value={category}
            loading={loadingCategories}
            groupBy={(option) => option?.speciality?.name || "Other"}
            getOptionLabel={(option) => option?.name || ""}
            isOptionEqualToValue={sameId}
            onChange={(event, next) => handleCategoryChange(next)}
            renderInput={(params) => (
              <TextField {...params} label="Category" required placeholder="Select a category" />
            )}
          />

          <Autocomplete
            multiple
            freeSolo
            size="small"
            disableCloseOnSelect
            filterSelectedOptions
            disabled={!category}
            options={linkedOptions}
            value={linkedTreatments}
            getOptionLabel={(option) =>
              typeof option === "string" ? option : linkedTreatmentLabel(option)
            }
            isOptionEqualToValue={sameLinkedTreatment}
            onChange={(event, next) => {
              // A typed name: the category's treatment of that name, else a custom one.
              const result = applyLinkedTreatmentChange(next, category);
              setLinkedTreatments(result.linked);
              setLinkedError(result.error);
            }}
            onInputChange={(event, value, reason) => {
              if (reason === "input") setLinkedError("");
            }}
            renderInput={(params) => (
              <TextField
                {...params}
                label="Linked Treatment"
                required
                placeholder={
                  category
                    ? "Select a treatment, or type a name and press Enter"
                    : "Select a category first"
                }
                error={Boolean(linkedError)}
                helperText={
                  linkedError ||
                  "Pick from the Treatment Master, or type a treatment it does not have."
                }
              />
            )}
          />

          <Autocomplete
            multiple
            freeSolo
            size="small"
            options={optionChoices}
            value={procedureOptions}
            getOptionLabel={(option) => (typeof option === "string" ? option : option?.name || "")}
            onChange={(event, next) => {
              const result = applyProcedureOptionChange(next);
              setProcedureOptions(result.options);
              setOptionError(result.error);
            }}
            onInputChange={(event, value, reason) => {
              if (reason === "input") setOptionError("");
            }}
            renderInput={(params) => (
              <TextField
                {...params}
                label="Procedure Options"
                required
                placeholder="Pick an option, or type one and press Enter"
                error={Boolean(optionError)}
                helperText={
                  optionError ||
                  `Press Enter to add each option (up to ${MAX_PROCEDURE_OPTIONS}).`
                }
              />
            )}
          />

          {validationError && (
            <Box role="alert" sx={{ color: "#d32f2f", fontSize: "0.8rem" }}>
              {validationError}
            </Box>
          )}
        </Box>
      </DialogContent>

      <DialogActions sx={{ padding: "1rem 1.5rem" }}>
        <Button onClick={() => onClose("close")} disabled={saving}>
          Cancel
        </Button>
        <Button
          variant="contained"
          onClick={handleSubmit}
          disabled={saving || Boolean(validationError)}
          sx={{ bgcolor: "#194AF5", ":hover": { bgcolor: "#1540d6" } }}
        >
          {saving ? <CircularProgress size="1.2rem" sx={{ color: "#fff" }} /> : "Save"}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default SpecialityProcedureModal;
